/**
 * №16 S4 — data hooks for the run surfaces (the ONE canonical workflow panel's
 * three densities + the W33 step card). Reads/mutations go through the same
 * `mcpClient.toolsCall` transport the ledger + time-billing workbenches use.
 *
 * The engine owns the projection; these hooks mirror its payload shapes locally
 * (the `use-time-billing.ts` convention — do not import server types into the
 * console bundle). All reads are the frozen S3 triad + the S4 read-only
 * `workflow_instances_for` resolver; every verb is a confirmation-free write on
 * the anchored record (execution lives in the App Shell, D-WF14).
 */
import type { NyOwner, NyDue, NyScope } from "../../lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

async function read<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  if (r.isError) throw new Error(r.content?.[0]?.text ?? `${name} failed`);
  return r.structuredContent as T;
}

// ---- shapes (mirror src/engine/wf/projection.ts) --------------------------

export interface ReadyStep { stepEntryNo: number; segmentNo: number; name: string; action: string; verb: string; assigneeKind: string; assigneeCode: string; dueDate: string | null; taskEntryNo: number | null; }
export interface WaitingStep { stepEntryNo: number; segmentNo: number; name: string; waitingOn: string; verb: string; }
export interface DecisionOutcome { code: string; label: string; isDefault: boolean; }
export interface DecisionPoint { segmentNo: number; decisionName: string; ripe: boolean; outcomes: DecisionOutcome[]; }
export interface OpenSegment { segmentNo: number; name: string; }
export interface BlockedStep { stepEntryNo: number; segmentNo: number; name: string; reason: string; }
/**
 * №16.2.1-B (D-RN1) — the ONE prioritised thing to do on this instance, the BC
 * FactBox lead ported with receipts (contract: docs/16-workflow-findings.md
 * §16.2.1-B). `rank` names the rule that fired, and is what the panel's minimal
 * "why this" affordance shows. `why` carries the date clause only — the surface
 * appends the RESOLVED assignee name (never the raw code).
 */
export type NextActionRank = "ripeDecision" | "overdue" | "atRisk" | "firstActionable" | "waitReceivable" | "nothingActionable";
export interface NextAction {
  kind: "decision" | "step" | "none";
  rank: NextActionRank;
  verb: string;
  title: string;
  why: string;
  segmentNo: number;
  segmentName: string;
  stepEntryNo: number;
  overdue: boolean;
  atRisk: boolean;
  dueDate: string | null;
  assigneeKind: string;
  assigneeCode: string;
  outcomes: DecisionOutcome[];
  /** №16.2.1 item 6 — the lead step's briefing, for the DO-NEXT one-liner. */
  instructions?: string;
}
export interface WhatsNext {
  found: boolean;
  instanceNo: number;
  status: string;
  done: boolean;
  subjectType: string; subjectNo: string;
  templateCode: string; templateVersion: number;
  description: string;
  ready: ReadyStep[];
  waiting: WaitingStep[];
  decisions: DecisionPoint[];
  openSegments: OpenSegment[];
  blocked: BlockedStep[];
  /** Optional ON PURPOSE: the console and the worker deploy separately, so a
   *  console can meet a worker that predates this field. Marking it optional makes
   *  TypeScript force every read to be guarded, instead of letting a missing field
   *  white-screen the matter cockpit. The server always sends it. */
  nextAction?: NextAction;
  /** RC1-3 — the ONE actionable set (ready + ripe decisions + waiting), derived
   *  once server-side. `actionable.line` is the honest glance; a surface may show
   *  "Nothing needs anyone" ONLY when `actionable.count === 0`. Optional for
   *  cross-deploy safety (a console can meet a worker that predates it). */
  actionable?: Actionable;
  endReason?: { code: string; text: string } | null;
  summary: string;
}
/** RC1-3 — the honest actionable set. `line`: positive form when non-empty
 *  ("6 things can move — 3 ready, 1 to decide, 2 waiting"), "" when empty. */
export interface Actionable { count: number; ready: number; decisions: number; waiting: number; line: string; }

export interface AnchorInstance {
  instanceNo: number;
  templateCode: string;
  templateVersion: number;
  status: string;
  done: boolean;
  description: string;
  startedAt: string | null;
  summary: string;
  readyCount: number;
  waitingCount: number;
  decisionCount: number;
  openSegmentCount: number;
  stage: string;           // №16.2 list glance — frontier segment name
  stepsDone: number;       // №16.2 — derived progress (n of m)
  stepsTotal: number;
  attention: string;       // №16.2 — ONE token: decision | onTrack | waiting | none
  originInstanceNo: number | null;
  successorInstanceNo: number | null;
}
export interface InstancesForAnchor {
  subjectType: string;
  subjectNo: string;
  count: number;
  instances: AnchorInstance[];
}

export interface DetailStep {
  stepEntryNo: number; name: string; status: string; action: string; verb: string;
  assigneeKind: string; assigneeCode: string; dueDate: string | null; optional: boolean; taskEntryNo: number | null;
  completedAt: string | null;   // №16.2.1 (D-RN3) — history renders dated, not just struck
  /** №16.2.1 item 6 — the step briefing (human or agent). Optional for cross-deploy safety. */
  instructions?: string;
  /** True when the instance briefing was edited away from the template — a deviation. */
  instructionsDeviation?: boolean;
  /** D-RS6 — the WRITE effects this step fires ("When this happens" rail). Optional
   *  for cross-deploy safety (a console can meet a worker that predates D-RS6). */
  effects?: DeclaredEffect[];
}
/** D-RS6 — a declared WRITE effect, tagged with its owner. `setAttribute` reporting
 *  writes carry attribute+value; other verbs carry the verb. */
export interface DeclaredEffect { ownerKind: string; ownerNo: number; outcomeCode: string | null; event: string; verb: string; attribute: string; value: string; }
export interface DetailDecision {
  name: string; ripe: boolean; resolved: boolean; outcomeCode: string | null; outcomes: DecisionOutcome[];
  /** №16.2.1 — WHEN the decision was recorded (the rail's "decided …"). Optional for cross-deploy safety. */
  decidedAt?: string | null;
}
export interface SegmentTrigger { kind: string; dependsOn?: number[]; outcome?: string; decisionSegmentNo?: number; dateType?: string; joinMode?: string; }
/**
 * №16.2.1-C (D-RN2) — ONE state truth.
 *
 * **Render `state`. Never compare `status`.** `status` is the raw engine literal
 * and is kept only for debugging: four consumers here used to test it against
 * `"completed"` while the engine writes `"complete"`, so every finished stage drew
 * itself as *future* while its own steps drew green ticks. That single mismatch is
 * most of what David saw on MAT-0026. `state` is derived once, server-side, and a
 * contract test asserts the bar and the body agree.
 *
 * `waiting` = the STAGE has not opened (its trigger has not fired) — not a step
 * awaiting a signal.
 */
export type SegmentState = "done" | "open" | "waiting" | "skipped";
/** D-RN4 — non-null means the state is IMPOSSIBLE. Flag it; never render it quietly. */
export interface SegmentStateIssue { code: string; message: string; }
export interface DetailSegment {
  segmentNo: number; name: string; status: string; hasDecision: boolean; decision: DetailDecision | null; steps: DetailStep[];
  state: SegmentState;              // №16.2.1 — the ONE field every element reads
  completedAt: string | null;       // №16.2.1 — WHEN it finished (done only)
  stateIssue: SegmentStateIssue | null;  // №16.2.1 — an impossible state, to be shown loudly
  stepsDone: number; stepsTotal: number; stepsOrdered: boolean;   // №16.2 stage progress + ordered/unordered
  /** Optional for the same reason as `nextAction`: a console can meet a worker
   *  that predates the №16.2 enrichment, and a missing structural field must
   *  degrade the chip, not blank the route. The server always sends it. */
  trigger?: SegmentTrigger;
  concurrentGroup: number | null; joinsFrom: number[] | null;  // №16.2 band + join
  /** D-RS6 — the segment's own WRITE effects (onOpen stamps). Optional for cross-deploy safety. */
  effects?: DeclaredEffect[];
}
export interface InstanceDetail {
  found: boolean;
  instanceNo: number;
  status: string;
  done: boolean;
  subjectType: string; subjectNo: string;
  templateCode: string; templateVersion: number;
  description: string;
  startedAt: string | null;
  endReason?: { code: string; text: string } | null;
  originInstanceNo: number | null;
  successorInstanceNo: number | null;
  segments: DetailSegment[];
  /** D-RS6 — every declared WRITE effect on this instance (decision/outcome
   *  effects too). The rail filters by the selected owner. Optional cross-deploy. */
  effects?: DeclaredEffect[];
}

// ---- Program (journey) read model (S5) ------------------------------------
export interface ProgramContextValue { intoInstanceNo: number; name: string; value: string; fromInstanceNo: number | null; }
export interface ProgramInstanceView {
  instanceNo: number;
  templateCode: string;
  templateVersion: number;
  status: string;
  done: boolean;
  subjectType: string;
  subjectNo: string;
  description: string;
  startedAt: string | null;
  isFrontier: boolean;
  originInstanceNo: number | null;
  successorInstanceNo: number | null;
  endingLabel: string | null;
  segments: DetailSegment[];
}
export interface Program {
  found: boolean;
  seedInstanceNo: number;
  rootInstanceNo: number;
  frontierInstanceNo: number;
  journeyLabel: string;
  instanceCount: number;
  crossesAnchors: boolean;
  anchors: { subjectType: string; subjectNo: string }[];
  carriedContext: ProgramContextValue[];
  instances: ProgramInstanceView[];
}

// ---- reads ----------------------------------------------------------------

/** The anchor→instances resolver — how a record surface finds its process(es). */
export function useAnchorWorkflows(subjectNo: string | undefined, subjectType = "matter") {
  return useQuery({
    queryKey: ["workflow", "instances-for", subjectType, subjectNo],
    enabled: !!subjectNo,
    staleTime: 10_000,
    queryFn: () => read<InstancesForAnchor>("workflow_instances_for", { subject_no: subjectNo, subject_type: subjectType }),
  });
}

/** The full run projection for one instance (panel + full-view density). */
export function useWhatsNext(instanceNo: number | undefined) {
  return useQuery({
    queryKey: ["workflow", "whats-next", instanceNo],
    enabled: instanceNo != null,
    staleTime: 10_000,
    queryFn: () => read<WhatsNext>("workflow_whats_next", { instance_no: instanceNo }),
  });
}

/** The ordered spine snapshot — backs the panel spine strip + the full view. */
export function useInstanceDetail(instanceNo: number | undefined) {
  return useQuery({
    queryKey: ["workflow", "instance-detail", instanceNo],
    enabled: instanceNo != null,
    staleTime: 10_000,
    queryFn: () => read<InstanceDetail>("workflow_instance_detail", { instance_no: instanceNo }),
  });
}

/** The Program (journey) across origin-linked instances — one timeline (W20). */
export function useProgramJourney(instanceNo: number | undefined) {
  return useQuery({
    queryKey: ["workflow", "program", instanceNo],
    enabled: instanceNo != null,
    staleTime: 10_000,
    queryFn: () => read<Program>("workflow_program", { instance_no: instanceNo }),
  });
}

export interface NeedsYouItem {
  instanceNo: number;
  subjectType: string; subjectNo: string;
  templateCode: string;
  kind: "decision" | "step" | "agent" | "wait";
  segmentNo: number;
  stepEntryNo: number;
  title: string;
  subtitle: string;
  verb: string;
  assigneeKind: string;
  assigneeCode: string;
  dueDate: string | null;
  waitingOn: string;
  outcomes: DecisionOutcome[];
  /**
   * №22.6 (D-NY1–4) — served, never derived here. The close-out found these rows
   * were ALREADY being sent `assigneeKind` / `assigneeCode` / `dueDate` /
   * `waitingOn` and dropping all four; the fix is not to compute more in the
   * console but to render what arrives. So the server now also sends the resolved
   * owner chip, the lateness, the why-now line, the scope verdict and the sort
   * rank — and this hook does nothing to any of them.
   */
  owner: NyOwner;
  due: NyDue;
  whyNow: string;
  scope: NyScope;
  rank: [number, number, number, number];
  rendersAsCard: boolean;
  since: string | null;
}
export interface WorkspaceNeedsYou {
  count: number;
  items: NeedsYouItem[];
  /** What the D-NY1 scope withheld, and why — Sprint 19's no-silent-truncation rule. */
  scope: { shown: number; filtered: number; byReason: Record<string, number> };
}

/**
 * Agent node-id → display name, so a step assigned to an agent shows the agent's
 * NAME (e.g. "Matter Assistant") rather than the raw `ln_…` node id. Cached long —
 * the roster changes rarely. Empty map is a safe fallback (rows show the code).
 */
export function useAgentDirectory() {
  return useQuery({
    queryKey: ["workflow", "agent-directory"],
    staleTime: 300_000,
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ agents: { id: string; name: string }[] }>("list_invocable_agents", {});
      const map: Record<string, string> = {};
      for (const a of r.structuredContent?.agents ?? []) map[a.id] = a.name;
      return map;
    },
  });
}

/** The workspace-wide workflow glance — the Needs-You inbox's workflow rows. */
export function useWorkspaceNeedsYou() {
  return useQuery({
    queryKey: ["workflow", "needs-you"],
    staleTime: 10_000,
    queryFn: () => read<WorkspaceNeedsYou>("workflow_needs_you", {}),
  });
}

// ---- verbs (confirmation-free writes; the engine is the single writer) -----

function verbMutation<V>(verb: string, buildArgs: (v: V) => Record<string, unknown>) {
  return function useVerb() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (v: V) => {
        const r = await mcpClient.toolsCall<WhatsNext>(verb, buildArgs(v));
        if (r.isError) throw new Error(r.content?.[0]?.text ?? `${verb} failed`);
        return r.structuredContent as WhatsNext;
      },
      // Any write moves the process — refresh both the instance projection and
      // the anchor's instance list (its next-summary counts may have changed).
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["workflow", "whats-next"] });
        void qc.invalidateQueries({ queryKey: ["workflow", "instance-detail"] });
        void qc.invalidateQueries({ queryKey: ["workflow", "instances-for"] });
        void qc.invalidateQueries({ queryKey: ["workflow", "needs-you"] });
        // №22.6 — a workflow write also moves a HIRE'S DESK: its queued steps come
        // from the same `wf_step_instances` rows. Without this the desk's own
        // Complete button ticked the step and left the card sitting there — a
        // press that appears to do nothing is worse than no press at all.
        void qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
        void qc.invalidateQueries({ queryKey: ["workforce-status"] });
      },
    });
  };
}

/** Complete a ready step (human attestation / mark-received on a wait). */
export const useWorkflowAdvance = verbMutation<{ instance_no: number; step_entry_no: number }>(
  "workflow_advance", (v) => ({ instance_no: v.instance_no, step_entry_no: v.step_entry_no }),
);
/** Resolve a decision — the chosen branch opens, others go dead. */
export const useWorkflowRecordOutcome = verbMutation<{ instance_no: number; segment_no: number; outcome: string; note?: string }>(
  "workflow_record_outcome", (v) => ({ instance_no: v.instance_no, segment_no: v.segment_no, outcome: v.outcome, ...(v.note ? { note: v.note } : {}) }),
);
/** Deliver a signal to release a matching wait (instance-scoped). */
export const useWorkflowSignal = verbMutation<{ instance_no: number; event_key: string }>(
  "workflow_signal", (v) => ({ instance_no: v.instance_no, event_key: v.event_key }),
);

// ─── №16.2.1 transfer verbs — the run-surface mutations (V1–V5 + item 6 + bulk) ───

/** V1 — un-decide a resolved decision (the drive view's "Clear choice"). */
export const useWorkflowClearOutcome = verbMutation<{ instance_no: number; segment_no: number }>(
  "workflow_clear_outcome", (v) => ({ instance_no: v.instance_no, segment_no: v.segment_no }),
);
/** V2 — skip a step with a reason (a run act; not a completion). */
export const useWorkflowSkipStep = verbMutation<{ instance_no: number; step_entry_no: number; reason?: string }>(
  "workflow_skip_step", (v) => ({ instance_no: v.instance_no, step_entry_no: v.step_entry_no, ...(v.reason ? { reason: v.reason } : {}) }),
);
/** V3 — reassign a step to a person, role, or agent. */
export const useWorkflowSetAssignee = verbMutation<{ instance_no: number; step_entry_no: number; assignee_kind: string; assignee_code?: string }>(
  "workflow_set_assignee", (v) => ({ instance_no: v.instance_no, step_entry_no: v.step_entry_no, assignee_kind: v.assignee_kind, ...(v.assignee_code ? { assignee_code: v.assignee_code } : {}) }),
);
/** V4 — pin (or clear with date:null) a manual due date. */
export const useWorkflowSetDue = verbMutation<{ instance_no: number; step_entry_no: number; date: string | null }>(
  "workflow_set_due", (v) => ({ instance_no: v.instance_no, step_entry_no: v.step_entry_no, date: v.date }),
);
/** V5 — set the user/agent-owned work state (never gates). */
export const useWorkflowSetWorkState = verbMutation<{ instance_no: number; step_entry_no: number; state: string }>(
  "workflow_set_work_state", (v) => ({ instance_no: v.instance_no, step_entry_no: v.step_entry_no, state: v.state }),
);
/** Item 6 — edit the instance step's instructions (a visible deviation). */
export const useWorkflowSetInstructions = verbMutation<{ instance_no: number; step_entry_no: number; instructions: string }>(
  "workflow_set_step_instructions", (v) => ({ instance_no: v.instance_no, step_entry_no: v.step_entry_no, instructions: v.instructions }),
);
/** V6 — bulk assign / set-due / mark-done / skip (the plan-lens bulk bar). */
export const useWorkflowBulkAssign = verbMutation<{ instance_no: number; step_entry_nos: number[]; assignee_kind: string; assignee_code?: string }>(
  "workflow_bulk_assign", (v) => ({ instance_no: v.instance_no, step_entry_nos: v.step_entry_nos, assignee_kind: v.assignee_kind, ...(v.assignee_code ? { assignee_code: v.assignee_code } : {}) }),
);
export const useWorkflowBulkSetDue = verbMutation<{ instance_no: number; step_entry_nos: number[]; date: string | null }>(
  "workflow_bulk_set_due", (v) => ({ instance_no: v.instance_no, step_entry_nos: v.step_entry_nos, date: v.date }),
);
export const useWorkflowBulkMarkDone = verbMutation<{ instance_no: number; step_entry_nos: number[] }>(
  "workflow_bulk_mark_done", (v) => ({ instance_no: v.instance_no, step_entry_nos: v.step_entry_nos }),
);
export const useWorkflowBulkSkip = verbMutation<{ instance_no: number; step_entry_nos: number[]; reason?: string }>(
  "workflow_bulk_skip", (v) => ({ instance_no: v.instance_no, step_entry_nos: v.step_entry_nos, ...(v.reason ? { reason: v.reason } : {}) }),
);
